import React from 'react';
import { Helmet } from 'react-helmet-async';
import QuizShell from '../../components/quiz/QuizShell';
import type { QuizConfig } from '../../components/quiz/QuizShell';

const config: QuizConfig = {
  niche: 'stress-symptoms',
  quizSlug: 'stress-symptoms',
  pageTitle: 'Are stress-related physical symptoms taking a toll on your daily life?',
  pageSubtitle: 'Answer 4 quick questions to understand your stress load and how hypnotherapy can help.',
  questions: [
    {
      id: 'frequency',
      question: 'How often do you experience physical symptoms like headaches, muscle tension, or disrupted sleep?',
      options: [
        { label: 'Occasionally — once a week or less', points: 1 },
        { label: 'A few times a week', points: 2 },
        { label: 'Almost every day', points: 3 },
      ],
    },
    {
      id: 'duration',
      question: 'How long have you been experiencing these physical symptoms?',
      options: [
        { label: 'A few weeks', points: 1 },
        { label: '1–3 months', points: 2 },
        { label: 'More than 3 months', points: 3 },
      ],
    },
    {
      id: 'impact',
      question: 'How much do these symptoms affect your daily activities?',
      options: [
        { label: 'A little — they\'re noticeable but manageable', points: 1 },
        { label: 'Moderately — they sometimes stop me doing things', points: 2 },
        { label: 'Significantly — they regularly affect my day', points: 3 },
      ],
    },
    {
      id: 'stress-level',
      question: 'How would you describe your general stress levels right now?',
      options: [
        { label: 'Mildly elevated — things feel a bit much', points: 1 },
        { label: 'Moderate — I often feel overwhelmed', points: 2 },
        { label: 'High — I rarely feel relaxed or at ease', points: 3 },
      ],
    },
  ],
  tiers: [
    {
      name: 'Mild',
      tierNumber: 1,
      minScore: 4,
      blurb:
        'Your symptoms suggest mild stress-related tension. Many people at this stage notice occasional headaches or disrupted sleep but can still function day-to-day. Hypnotherapy can help you break the habit loop before symptoms escalate.',
    },
    {
      name: 'Moderate',
      tierNumber: 2,
      minScore: 7,
      blurb:
        'Your symptoms point to a moderate stress load that your body is carrying as physical tension. The pattern of how long this has been building matters — and hypnotherapy works directly with the mind-body connection that drives these symptoms.',
    },
    {
      name: 'Significant',
      tierNumber: 3,
      minScore: 10,
      blurb:
        'Your results suggest your body has been carrying significant stress-related strain for some time. Hypnotherapy is particularly effective for long-standing patterns because it works at the level where those patterns are held — and relief is often faster than people expect.',
    },
  ],
};

const StressSymptomQuiz: React.FC = () => {
  return (
    <>
      <Helmet>
        <title>Stress Symptoms Quiz | Sharon Clare Hypnotherapy</title>
        <meta
          name="description"
          content="Take our short quiz to understand your stress-related physical symptoms and find out how hypnotherapy can help."
        />
        <link rel="canonical" href="https://www.sharonclare-hypnotherapy.com/quiz/stress-symptoms/" />
      </Helmet>
      <QuizShell config={config} />
    </>
  );
};

export default StressSymptomQuiz;
